import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react/dist/iconify.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import MitraStatistics from './child/MitraStatistics';
import BookingOverview from './child/BookingOverview';
import TopMitras from './child/TopMitras';
import LatestBookings from './child/LatestBookings';
import ServicePerformance from './child/ServicePerformance';


const DashBoardLayerOne = () => {
    // State Management
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState({
        totalMitras: 0,
        activeMitras: 0,
        totalBookings: 0,
        completedBookings: 0,
        pendingBookings: 0,
        totalRevenue: 0,
        thisMonthRevenue: 0,
        lastMonthRevenue: 0,
        topMitras: [],
        latestBookings: [],
        servicePerformance: [],
        locationData: [],
        mitraBalance: []
    });

    // API config
    const API_BASE_URL = 'https://api.tomassage.id/api';

    // Fetch dashboard data
    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            
            // Fetch mitras
            const mitrasResponse = await axios.get(`${API_BASE_URL}/mitra`);
            const mitras = mitrasResponse.data.data || [];
            
            // Fetch all bookings
            const bookingsResponse = await axios.get(`${API_BASE_URL}/bookings`);
            const bookings = bookingsResponse.data.data || [];
            
            // Calculate statistics
            const activeMitras = mitras.filter(mitra => mitra.status === 'active').length;
            const completedBookings = bookings.filter(booking => booking.status === 'Completed').length;
            const pendingBookings = bookings.filter(booking => booking.status === 'Pending').length;
            
            // Calculate revenue
            const totalRevenue = bookings
                .filter(booking => booking.status === 'Completed')
                .reduce((sum, booking) => sum + booking.harga_service, 0);
            
            // Calculate this month and last month revenue
            const now = new Date();
            const thisMonth = now.getMonth();
            const thisYear = now.getFullYear();
            const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
            const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;
            
            const thisMonthRevenue = bookings
                .filter(booking => {
                    const bookingDate = new Date(booking.created_at);
                    return booking.status === 'Completed' && 
                           bookingDate.getMonth() === thisMonth && 
                           bookingDate.getFullYear() === thisYear;
                })
                .reduce((sum, booking) => sum + booking.harga_service, 0);
            
            const lastMonthRevenue = bookings
                .filter(booking => {
                    const bookingDate = new Date(booking.created_at);
                    return booking.status === 'Completed' && 
                           bookingDate.getMonth() === lastMonth && 
                           bookingDate.getFullYear() === lastMonthYear;
                })
                .reduce((sum, booking) => sum + booking.harga_service, 0);
            
            // Get top mitras by completed bookings
            const mitraBookings = mitras.map(mitra => {
                const mitraCompletedBookings = bookings.filter(
                    booking => booking.id_mitra === mitra.id && booking.status === 'Completed'
                );
                const revenue = mitraCompletedBookings.reduce(
                    (sum, booking) => sum + booking.harga_service, 0
                );
                
                return {
                    ...mitra,
                    completedBookings: mitraCompletedBookings.length,
                    revenue
                };
            });
            
            const topMitras = mitraBookings
                .sort((a, b) => b.revenue - a.revenue)
                .slice(0, 5);
            
            // Get latest bookings
            const latestBookings = [...bookings]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 5);
            
            // Calculate service performance
            const serviceGroups = bookings.reduce((groups, booking) => {
                const service = booking.nama_service;
                if (!groups[service]) {
                    groups[service] = {
                        name: service,
                        count: 0,
                        revenue: 0
                    };
                }
                groups[service].count += 1;
                groups[service].revenue += booking.harga_service;
                return groups;
            }, {});
            
            const servicePerformance = Object.values(serviceGroups)
                .sort((a, b) => b.revenue - a.revenue)
                .slice(0, 5);
            
            // Prepare location data
            const locationData = bookings.map(booking => ({
                lat: booking.latitude_user,
                lng: booking.longitude_user,
                weight: booking.harga_service / 100000 // Normalize for heatmap
            })).filter(loc => loc.lat && loc.lng);
            
            // Update state with calculated data
            setDashboardData({
                totalMitras: mitras.length,
                activeMitras,
                totalBookings: bookings.length,
                completedBookings,
                pendingBookings,
                totalRevenue,
                thisMonthRevenue,
                lastMonthRevenue,
                topMitras,
                latestBookings,
                servicePerformance,
                locationData,
                mitraBalance: mitras.map(m => ({
                    id: m.id,
                    name: m.nama_lengkap,
                    balance: m.balance || 0
                })).sort((a, b) => b.balance - a.balance).slice(0, 5)
            });
            
            setError(null);
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchDashboardData();
        
        // Set up refresh interval
        const interval = setInterval(fetchDashboardData, 5 * 60 * 1000); // refresh every 5 minutes
        
        return () => clearInterval(interval);
    }, []);

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Show loading state
    if (loading && !dashboardData.totalMitras) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Error Banner */}
            {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setError(null)}
                    ></button>
                </div>
            )}
            
            {/* Dashboard Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <h2 className="mb-0">Dashboard</h2>
                        <button 
                            className="btn btn-outline-primary d-flex align-items-center gap-2"
                            onClick={fetchDashboardData}
                        >
                            <Icon icon="mdi:refresh" />
                            Refresh Data
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Stat Cards */}
            <div className="row g-4 mb-4">
                <div className="col-xl-3 col-md-6">
                    <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-3">
                                <div className="p-3 bg-primary bg-opacity-10 rounded-circle me-3">
                                    <Icon icon="mdi:account-group" style={{ fontSize: '1.5rem' }} className="text-primary" />
                                </div>
                                <h6 className="fw-bold mb-0">Total Mitra</h6>
                            </div>
                            <div className="d-flex justify-content-between align-items-end">
                                <h3 className="fw-bold mb-0">{dashboardData.totalMitras}</h3>
                                <span className="text-success">
                                    <span className="badge bg-success-subtle text-success">
                                        {dashboardData.activeMitras} Active
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-md-6">
                    <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-3">
                                <div className="p-3 bg-success bg-opacity-10 rounded-circle me-3">
                                    <Icon icon="mdi:calendar-check" style={{ fontSize: '1.5rem' }} className="text-success" />
                                </div>
                                <h6 className="fw-bold mb-0">Total Bookings</h6>
                            </div>
                            <div className="d-flex justify-content-between align-items-end">
                                <h3 className="fw-bold mb-0">{dashboardData.totalBookings}</h3>
                                <span className="text-primary">
                                    <span className="badge bg-success-subtle text-success me-1">
                                        {dashboardData.completedBookings} Completed
                                    </span>
                                    <span className="badge bg-warning-subtle text-warning">
                                        {dashboardData.pendingBookings} Pending
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-md-6">
                    <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-3">
                                <div className="p-3 bg-warning bg-opacity-10 rounded-circle me-3">
                                    <Icon icon="mdi:cash" style={{ fontSize: '1.5rem' }} className="text-warning" />
                                </div>
                                <h6 className="fw-bold mb-0">Month Revenue</h6>
                            </div>
                            <div className="d-flex justify-content-between align-items-end">
                                <h3 className="fw-bold mb-0">{formatCurrency(dashboardData.thisMonthRevenue)}</h3>
                                <span className={dashboardData.thisMonthRevenue >= dashboardData.lastMonthRevenue ? 'text-success' : 'text-danger'}>
                                    <Icon 
                                        icon={dashboardData.thisMonthRevenue >= dashboardData.lastMonthRevenue ? 'mdi:arrow-up' : 'mdi:arrow-down'} 
                                        className="me-1" 
                                    />
                                    {Math.abs(((dashboardData.thisMonthRevenue - dashboardData.lastMonthRevenue) / (dashboardData.lastMonthRevenue || 1)) * 100).toFixed(1)}%
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-md-6">
                    <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-3">
                                <div className="p-3 bg-info bg-opacity-10 rounded-circle me-3">
                                    <Icon icon="mdi:chart-line" style={{ fontSize: '1.5rem' }} className="text-info" />
                                </div>
                                <h6 className="fw-bold mb-0">Total Revenue</h6>
                            </div>
                            <div className="d-flex justify-content-between align-items-end">
                                <h3 className="fw-bold mb-0">{formatCurrency(dashboardData.totalRevenue)}</h3>
                                <span className="text-muted small">Lifetime</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                {/* MitraStatistics */}
                <div className="col-lg-8">
                    <MitraStatistics data={dashboardData} formatCurrency={formatCurrency} />
                </div>

                {/* BookingOverview */}
                <div className="col-lg-4">
                    <BookingOverview data={dashboardData} />
                </div>

                {/* RevenueAnalytics */}
              

               

                {/* LatestBookings */}
                <div className="col-lg-6">
                    <LatestBookings bookings={dashboardData.latestBookings} formatCurrency={formatCurrency} />
                </div>

                {/* TopMitras */}
                <div className="col-lg-6">
                    <TopMitras mitras={dashboardData.topMitras} formatCurrency={formatCurrency} />
                </div>

                {/* ServicePerformance */}
                <div className="col-lg-6">
                    <ServicePerformance services={dashboardData.servicePerformance} formatCurrency={formatCurrency} />
                </div>

              
            </div>
        </>
    );
};

export default DashBoardLayerOne;